import { useCurrency } from "@/lib/currency";
// Calendario propio V2: disponibilidad, horarios, cupos, categorías de
// participantes (con sus rangos de edad), precio por categoría, moneda y total,
// todo tal como lo devuelve la API de Bókun.
//
// Nada está escrito en el código: fechas, horarios, cupos, categorías, edades y
// precios provienen de `getAvailability`, `getPriceList` y del propio producto.
import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Clock, Minus, Plus, Loader2 } from "lucide-react";

import { getAvailability, getPriceList } from "@/lib/bokun.functions";
import { categoryPricesForRate } from "@/lib/bokun-test/pricing";
import { trackTourV2 } from "@/lib/tour-v2-analytics";

import { useFormatPrice } from "@/hooks/use-format-price";
import { cn } from "@/lib/utils";
import {
  ageLabel,
  rowAmount,
  type TourV2PriceRow,
  type TourV2Quote,
  type TourV2Selection,
} from "@/components/tour/v2/tour-v2-selection";
import { useState } from "react";

export interface TourV2BookingCardProps {
  activityId: string | null;
  priceFrom: number | null;
  currency: string;
  /** Categorías reales del producto (id, título, edades, tipo de boleto). */
  pricingCategories: {
    id: string;
    title: string;
    minAge: number | null;
    maxAge: number | null;
    ticketCategory?: string | null;
    groupSize?: number | null;
  }[];
  /** Cargos obligatorios estructurados (concepto + importe). Vacío = no mostrar. */
  mandatoryFees?: { id: string; title: string; price: number; currency: string | null }[];
  minBookable: number | null;
  maxBookable: number | null;
  selection: TourV2Selection;
  onSelectionChange: (next: TourV2Selection) => void;
  onQuoteChange?: (quote: TourV2Quote) => void;
  /** Continúa al contenedor único de reservación (datos → pago). */
  onContinue: (quote: TourV2Quote) => void;
  compact?: boolean;
  /** Oculta el botón principal (cuando el contenedor ya tiene el suyo). */
  hideCta?: boolean;
}

function ymd(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function TourV2BookingCard(props: TourV2BookingCardProps) {
  useCurrency(); // subscribe: formatPrice reads the currency store
  const { t, i18n } = useTranslation();
  const formatPrice = useFormatPrice();
  const sel = props.selection;
  const [monthOffset, setMonthOffset] = useState(0);

  const today = useMemo(() => new Date(), []);
  const month = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + monthOffset, 1),
    [today, monthOffset],
  );

  const range = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const start = first < today ? today : first;
    return { start: ymd(start), end: ymd(last) };
  }, [month, today]);

  const availability = useQuery({
    queryKey: ["bokun", "availability", props.activityId, range.start, range.end, props.currency],
    enabled: !!props.activityId,
    staleTime: 60_000,
    queryFn: () =>
      getAvailability({
        data: {
          id: props.activityId!,
          start: range.start,
          end: range.end,
          currency: props.currency,
        },
      }),
  });

  const priceList = useQuery({
    queryKey: ["bokun", "price-list", props.activityId],
    enabled: !!props.activityId,
    staleTime: 5 * 60_000,
    queryFn: () => getPriceList({ data: { id: props.activityId! } }),
  });

  // Sonda de 90 días: abre el calendario en el primer mes con salidas reales.
  const probeRange = useMemo(() => {
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 90);
    return { start: ymd(today), end: ymd(end) };
  }, [today]);

  const probe = useQuery({
    queryKey: ["bokun", "availability", props.activityId, "probe90", probeRange.start],
    enabled: !!props.activityId,
    staleTime: 5 * 60_000,
    queryFn: () =>
      getAvailability({
        data: {
          id: props.activityId!,
          start: probeRange.start,
          end: probeRange.end,
          currency: props.currency,
        },
      }),
  });

  const firstAvailable = useMemo(() => {
    const d = (probe.data?.days ?? []).find((x) => x.date && x.available && !x.soldOut);
    return d?.date?.slice(0, 10) ?? null;
  }, [probe.data]);

  const [jumped, setJumped] = useState(false);
  useEffect(() => {
    if (jumped || !firstAvailable) return;
    const [y, m] = firstAvailable.split("-").map(Number);
    const offset = (y - today.getFullYear()) * 12 + (m - 1 - today.getMonth());
    if (offset > 0) setMonthOffset(offset);
    setJumped(true);
  }, [firstAvailable, jumped, today]);

  const days = availability.data?.days ?? [];

  const byDate = useMemo(() => {
    const map = new Map<string, typeof days>();
    for (const d of days) {
      if (!d.date || !d.available || d.soldOut) continue;
      const key = d.date.slice(0, 10);
      map.set(key, [...(map.get(key) ?? []), d]);
    }
    return map;
  }, [days]);

  const slots = useMemo(() => (sel.date ? (byDate.get(sel.date) ?? []) : []), [sel.date, byDate]);
  const activeSlot = slots.find((s) => s.startTimeId != null && s.startTimeId === sel.startTimeId);

  /** Nombre de la categoría en el idioma activo; si no hay equivalente, el de la API. */
  const catLabel = (title: string | null, ticketCategory?: string | null) => {
    const key = (ticketCategory ?? "").toUpperCase().trim();
    if (key) {
      const translated = t(`tourV2.cat.${key}`, { defaultValue: "" }) as string;
      if (translated) return translated;
    }
    return title ?? (t("tourV2.travellers", { defaultValue: "Viajeros" }) as string);
  };

  /**
   * Precio por reserva (grupo, vehículo, embarcación, privado): Bókun lo indica
   * con `pricePerBooking` en la tarifa y sin precios por categoría. Nunca se
   * deduce del título del tour.
   */
  const groupPrice = useMemo(() => {
    const source =
      activeSlot ??
      days.find((d) => d.available && !d.soldOut) ??
      (probe.data?.days ?? []).find((d) => d.available && !d.soldOut) ??
      null;
    const pbr = Array.isArray(source?.pricesByRate)
      ? (source!.pricesByRate as Record<string, unknown>[])
      : [];
    for (const e of pbr) {
      const per = (e["pricePerBooking"] ?? null) as Record<string, unknown> | null;
      const units = Array.isArray(e["pricePerCategoryUnit"]) ? (e["pricePerCategoryUnit"] as unknown[]) : [];
      const amount = per && typeof per["amount"] === "number" ? (per["amount"] as number) : null;
      if (amount != null && amount > 0 && units.length === 0) {
        return {
          amount,
          currency: typeof per!["currency"] === "string" ? (per!["currency"] as string) : props.currency,
        };
      }
    }
    return null;
  }, [activeSlot, days, probe.data, props.currency]);

  /** Categorías reales con precio real. Sin precio de la API, no se muestran. */
  const rows: TourV2PriceRow[] = useMemo(() => {
    const meta = new Map(props.pricingCategories.map((c) => [String(c.id), c]));

    // 0) Precio por grupo: una sola unidad, las personas no multiplican el precio.
    if (groupPrice) {
      const cat =
        props.pricingCategories.find((c) => (c.ticketCategory ?? "").toUpperCase() === "GROUP") ??
        props.pricingCategories[0];
      if (cat) {
        return [
          {
            id: String(cat.id),
            title: t("tourV2.privateGroup", { defaultValue: "Grupo privado" }),
            minAge: null,
            maxAge: null,
            price: groupPrice.amount,
            currency: groupPrice.currency,
            perBooking: true,
            groupMax: cat.groupSize ?? props.maxBookable ?? null,
          },
        ];
      }
    }

    // 1) Cotización real de la salida elegida (fecha + horario), cuando existe.
    const pbr = Array.isArray(activeSlot?.pricesByRate)
      ? (activeSlot!.pricesByRate as Record<string, unknown>[])
      : [];
    if (pbr.length > 0) {
      const first = pbr[0] as Record<string, unknown>;
      const prices =
        categoryPricesForRate(pbr, null).length > 0
          ? categoryPricesForRate(pbr, null)
          : categoryPricesForRate(pbr, (first["activityRateId"] ?? first["rateId"]) as string);
      // Importante: NO filtramos por precio. Una categoría reservable con
      // precio 0 (por ejemplo bebés) debe mostrarse como "Gratis".
      const fromSlot = prices
        .filter((p) => Number.isFinite(p.amount) && p.amount >= 0)
        .map((p) => {
          const c = meta.get(p.pricingCategoryId);
          return {
            id: p.pricingCategoryId,
            title: catLabel(c?.title ?? null, c?.ticketCategory),
            minAge: c?.minAge ?? null,
            maxAge: c?.maxAge ?? null,
            price: p.amount,
            currency: p.currency || props.currency,
          } satisfies TourV2PriceRow;
        });
      if (fromSlot.length > 0) {
        if (import.meta.env.DEV) {
          console.debug("[tourV2] categorías reservables de Bókun para la salida", {
            date: sel.date,
            startTimeId: sel.startTimeId,
            categories: fromSlot.map((r) => ({
              id: r.id,
              title: r.title,
              minAge: r.minAge,
              maxAge: r.maxAge,
              price: r.price,
              currency: r.currency,
            })),
          });
        }
        return fromSlot;
      }
    }

    // 2) Lista de precios del producto (mientras no hay salida elegida).
    const entries = (priceList.data?.entries ?? []).filter((e) => e.price != null && e.price >= 0);
    const byId = new Map(entries.map((e) => [String(e.pricingCategoryId), e]));
    const fromProduct = props.pricingCategories
      .map((c) => {
        const e = byId.get(String(c.id));
        if (!e || e.price == null) return null;
        return {
          id: String(c.id),
          title: catLabel(c.title, c.ticketCategory ?? e.ticketCategory),
          minAge: c.minAge,
          maxAge: c.maxAge,
          price: e.price,
          currency: e.currency ?? props.currency,
        } satisfies TourV2PriceRow;
      })
      .filter(Boolean) as TourV2PriceRow[];
    if (fromProduct.length > 0) return fromProduct;

    const fromPriceList = entries.map(
      (e) =>
        ({
          id: String(e.pricingCategoryId),
          title: catLabel(e.pricingCategoryTitle, e.ticketCategory),
          minAge: null,
          maxAge: null,
          price: e.price as number,
          currency: e.currency ?? props.currency,
        }) satisfies TourV2PriceRow,
    );
    if (fromPriceList.length > 0) return fromPriceList;

    // Último recurso real: el precio por persona de la propia salida.
    const slotPrice = activeSlot?.price ?? slots.find((s) => (s.price ?? 0) > 0)?.price ?? null;
    const anyPrice = slotPrice ?? props.priceFrom;
    if (anyPrice && anyPrice > 0) {
      return [
        {
          id: "default",
          title: t("tourV2.travellers", { defaultValue: "Viajeros" }),
          minAge: null,
          maxAge: null,
          price: anyPrice,
          currency: props.currency,
        },
      ];
    }
    return [];
  }, [
    groupPrice,
    priceList.data,
    props.pricingCategories,
    props.currency,
    props.priceFrom,
    activeSlot,
    slots,
    t,
  ]);

  const currency = rows[0]?.currency ?? props.currency;
  const pax = rows.reduce((s, r) => s + (sel.qty[r.id] ?? 0), 0);
  const total = rows.reduce((s, r) => s + rowAmount(r, sel.qty[r.id] ?? 0), 0);
  const group = rows.length === 1 && rows[0].perBooking ? rows[0] : null;
  const minPax = Math.max(1, props.minBookable ?? 1);
  const maxPax = group?.groupMax ?? props.maxBookable ?? activeSlot?.availabilityCount ?? 20;
  const perGroup = t("tourV2.perGroup", { defaultValue: "por grupo" }) as string;
  // "Continuar a reservar" sólo cuando hay fecha, horario, participante válido,
  // precio real recibido y la salida sigue disponible en la API.
  const slotStillAvailable = !!activeSlot && !activeSlot.soldOut && activeSlot.available !== false;
  const complete =
    !!sel.date &&
    !!sel.startTimeId &&
    pax >= minPax &&
    pax <= maxPax &&
    total > 0 &&
    slotStillAvailable;
  const priced = !!sel.date && !!sel.startTimeId && total > 0;
  // Categoría principal según Bókun (ticketCategory ADULT); si no existe, la
  // primera con precio. "Desde" usa su precio real, no el de niños o bebés.
  const adultId =
    props.pricingCategories.find((c) => (c.ticketCategory ?? "").toUpperCase() === "ADULT")?.id ??
    null;
  const mainRow =
    rows.find((r) => adultId != null && r.id === String(adultId) && r.price > 0) ??
    rows.find((r) => r.price > 0) ??
    rows[0];
  const fromPrice = mainRow && mainRow.price > 0 ? mainRow.price : props.priceFrom;

  const quote: TourV2Quote = useMemo(
    () => ({ selection: sel, rows, currency, total, pax, complete }),
    [sel, rows, currency, total, pax, complete],
  );

  // Sólo avisamos al contenedor cuando el presupuesto cambia de verdad:
  // así evitamos bucles de re-render si las referencias se recrean.
  const lastQuoteSig = useRef<string>("");
  useEffect(() => {
    const sig = JSON.stringify(quote);
    if (sig === lastQuoteSig.current) return;
    lastQuoteSig.current = sig;
    props.onQuoteChange?.(quote);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote]);

  // Primera categoría con el mínimo del producto. Si las categorías reales
  // llegan después (o cambian), reencaminamos la cantidad a una categoría válida.
  const rowIdsKey = rows.map((r) => r.id).join("|");
  useEffect(() => {
    if (rows.length === 0) return;
    const valid = Object.entries(sel.qty).filter(
      ([id, q]) => rows.some((r) => r.id === id) && q > 0,
    );
    if (valid.length > 0) {
      if (valid.length !== Object.keys(sel.qty).length) {
        props.onSelectionChange({ ...sel, qty: Object.fromEntries(valid) });
      }
      return;
    }
    props.onSelectionChange({ ...sel, qty: { [(mainRow ?? rows[0]).id]: minPax } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowIdsKey]);

  const cells = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7;
    const totalDays = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const out: (string | null)[] = Array.from({ length: lead }, () => null);
    for (let day = 1; day <= totalDays; day++) {
      out.push(ymd(new Date(month.getFullYear(), month.getMonth(), day)));
    }
    return out;
  }, [month]);

  const monthLabel = new Intl.DateTimeFormat(i18n.language, { month: "long", year: "numeric" })
    .format(month)
    .replace(/^./, (c) => c.toLocaleUpperCase(i18n.language));
  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.language, { weekday: "short" });
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2026, 5, 1 + i)).slice(0, 2));
  }, [i18n.language]);

  const todayKey = ymd(today);
  const fees = (props.mandatoryFees ?? []).filter((f) => f.price > 0 && f.title);

  const setQty = (id: string, next: number) => {
    const others = rows.reduce((s, r) => (r.id === id ? s : s + (sel.qty[r.id] ?? 0)), 0);
    const capped = Math.max(0, Math.min(next, Math.max(0, maxPax - others)));
    props.onSelectionChange({ ...sel, qty: { ...sel.qty, [id]: capped } });
    trackTourV2("participants_changed", { categories: rows.length, pax: others + capped });
  };

  return (
    <div
      className={cn(
        "rounded-3xl border border-border/70 bg-card shadow-[0_2px_10px_rgba(23,23,23,0.05)]",
        props.compact ? "p-4" : "p-5",
      )}
    >
      <h2 className="tour-display text-lg">
        {t("tourV2.pickDay", { defaultValue: "Elige tu día" })}
      </h2>

      {/* Calendario */}
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setMonthOffset((v) => Math.max(0, v - 1))}
            disabled={monthOffset === 0}
            aria-label={t("common.previous", { defaultValue: "Anterior" }) as string}
            className="grid h-9 w-9 place-items-center rounded-full border border-border text-foreground transition hover:bg-accent disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-semibold">{monthLabel}</span>
          <button
            type="button"
            onClick={() => setMonthOffset((v) => Math.min(11, v + 1))}
            aria-label={t("common.next", { defaultValue: "Siguiente" }) as string}
            className="grid h-9 w-9 place-items-center rounded-full border border-border text-foreground transition hover:bg-accent"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[0.65rem] uppercase tracking-wide text-muted-foreground">
          {weekdays.map((w, i) => (
            <span key={`wd-${i}`}>{w}</span>
          ))}
        </div>

        <div className="mt-1 grid grid-cols-7 gap-1" role="grid">
          {cells.map((key, i) => {
            if (!key) return <span key={`empty-${i}`} />;
            const has = byDate.has(key);
            const past = key < todayKey;
            const selected = sel.date === key;
            return (
              <button
                key={key}
                type="button"
                role="gridcell"
                disabled={!has || past}
                aria-pressed={selected}
                aria-label={key}
                onClick={() =>
                  props.onSelectionChange({
                    ...sel,
                    date: key,
                    startTimeId: byDate.get(key)?.[0]?.startTimeId ?? null,
                    startTime: byDate.get(key)?.[0]?.startTime ?? null,
                  })
                }
                className={cn(
                  "relative grid h-9 w-full place-items-center rounded-full text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  selected
                    ? "bg-primary font-bold text-primary-foreground"
                    : has && !past
                      ? "font-semibold text-foreground hover:bg-accent"
                      : "text-muted-foreground/40",
                )}
              >
                {Number(key.slice(8, 10))}
                {has && !selected && !past && (
                  <span className="absolute bottom-1 h-1 w-1 rounded-full bg-primary" aria-hidden />
                )}
              </button>
            );
          })}
        </div>

        {availability.isLoading && (
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t("tourV2.loadingDates", { defaultValue: "Consultando disponibilidad real…" })}
          </p>
        )}
        {!availability.isLoading && byDate.size === 0 && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t("tourV2.noDates", { defaultValue: "No hay fechas disponibles en este mes." })}
          </p>
        )}
      </div>

      {/* Horarios reales, con cupo cuando la API lo entrega */}
      {slots.length > 0 && (
        <div className="mt-4">
          <h3 className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {t("tourV2.times", { defaultValue: "Horarios disponibles" })}
          </h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {slots.map((s, i) => {
              const active = s.startTimeId != null && s.startTimeId === sel.startTimeId;
              return (
                <button
                  key={`${s.startTime ?? i}`}
                  type="button"
                  onClick={() =>
                    props.onSelectionChange({
                      ...sel,
                      startTimeId: s.startTimeId ?? null,
                      startTime: s.startTime ?? null,
                    })
                  }
                  aria-pressed={active}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition",
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border hover:bg-accent",
                  )}
                >
                  <Clock className="h-3.5 w-3.5" />
                  {s.startTime ?? "—"}
                  {s.availabilityCount != null && (
                    <span className="text-xs font-normal text-muted-foreground">
                      · {s.availabilityCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Categorías reales de participantes */}
      <div className="mt-4 space-y-2">
        <h3 className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {t("tourV2.participants", { defaultValue: "Participantes" })}
        </h3>
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t("tourV2.noPrices", {
              defaultValue: "El proveedor aún no publica precios para esta fecha.",
            })}
          </p>
        ) : (
          rows.map((r) => {
            const ages = ageLabel(r);
            const value = sel.qty[r.id] ?? 0;
            return (
              <div
                key={r.id}
                className="flex items-center justify-between gap-3 rounded-2xl border border-border/70 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">
                    {r.title}
                    {ages && (
                      <span className="ml-1.5 font-normal text-muted-foreground">
                        {t("tourV2.age", { defaultValue: "edad" })} {ages}
                      </span>
                    )}
                  </p>
                  {r.perBooking && r.groupMax != null && (
                    <p className="text-xs text-muted-foreground">
                      {t("tourV2.upToPeople", {
                        count: r.groupMax,
                        defaultValue: `Hasta ${r.groupMax} personas`,
                      })}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {r.price > 0
                      ? `${formatPrice(r.price, r.currency)} ${r.currency}${r.perBooking ? ` ${perGroup}` : ""}`
                      : t("tourV2.free", { defaultValue: "Gratis" })}
                  </p>
                  {r.perBooking && (
                    <p className="text-[0.7rem] text-muted-foreground">
                      {t("tourV2.howManyPeople", { defaultValue: "¿Cuántas personas viajarán?" })}
                    </p>
                  )}
                </div>
                <span className="inline-flex shrink-0 items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setQty(r.id, value - 1)}
                    disabled={value <= 0}
                    aria-label={`${t("tourV2.lessTravellers", { defaultValue: "Quitar" })} ${r.title}`}
                    className="grid h-8 w-8 place-items-center rounded-full border border-border transition hover:bg-accent disabled:opacity-40"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-6 text-center text-sm font-bold" aria-live="polite">
                    {value}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQty(r.id, value + 1)}
                    disabled={r.perBooking ? value >= maxPax : false}
                    aria-label={`${t("tourV2.moreTravellers", { defaultValue: "Añadir" })} ${r.title}`}
                    className="grid h-8 w-8 place-items-center rounded-full border border-border transition hover:bg-accent disabled:opacity-40"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </span>
              </div>
            );
          })
        )}
        {group && pax >= maxPax && (
          <p className="text-xs text-muted-foreground" role="status">
            {t("tourV2.groupLimit", {
              count: maxPax,
              defaultValue: `El precio cubre un grupo de hasta ${maxPax} personas. Para más personas, contáctanos.`,
            })}
          </p>
        )}
        {pax > 0 && pax < minPax && (
          <p className="text-xs text-destructive">
            {t("tourV2.minPax", {
              count: minPax,
              defaultValue: `Mínimo ${minPax} participantes.`,
            })}
          </p>
        )}
      </div>

      {/* Antes de elegir salida: "Desde". Con salida elegida: "Total" real. */}
      <div className="mt-4 flex items-end justify-between gap-3 border-t border-border/70 pt-3">
        <div>
          <p className="text-[0.7rem] uppercase tracking-[0.12em] text-muted-foreground">
            {priced || pax > 0
              ? t("tourV2.total", { defaultValue: "Total" })
              : t("tour.from", { defaultValue: "Precio" })}
          </p>
          <p className="tour-display text-2xl leading-tight">
            {priced || pax > 0
              ? formatPrice(total, currency)
              : fromPrice != null
                ? formatPrice(fromPrice, currency)
                : "—"}
          </p>
          <p className="text-xs text-muted-foreground">
            {currency}
            {group ? ` ${perGroup}` : ""}
          </p>
        </div>
      </div>

      {/* Cargos obligatorios: sólo si la API los entrega con concepto e importe */}
      {fees.length > 0 && (
        <div className="mt-3 rounded-2xl bg-muted/50 p-3 text-xs leading-relaxed">
          <p className="font-semibold">
            {t("tourV2.feesAtDestination", {
              defaultValue: "Cargos que se pagan en el destino",
            })}
          </p>
          <ul className="mt-1.5 space-y-1">
            {fees.map((f) => (
              <li key={f.id} className="flex justify-between gap-3">
                <span>{f.title}</span>
                <span className="font-semibold">
                  {formatPrice(f.price, f.currency ?? currency)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!props.hideCta && (
        <button
          type="button"
          disabled={!complete}
          onClick={() => props.onContinue(quote)}
          className="mt-4 w-full rounded-full bg-primary px-5 py-3 text-sm font-bold text-primary-foreground shadow-sm transition hover:brightness-105 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          {complete
            ? t("tourV2.bookNowCta", { defaultValue: "Reservar ahora" })
            : !sel.date
              ? t("tourV2.needDate", { defaultValue: "Selecciona una fecha" })
              : !sel.startTimeId
                ? t("tourV2.needTime", { defaultValue: "Selecciona un horario" })
                : t("tourV2.needPax", { defaultValue: "Añade viajeros" })}
        </button>
      )}
    </div>
  );
}
